// File: frontend/src/components/PersonDetailModal.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { optionLabel } from '../utils/optionLabels';
import { isolateList } from '../utils/bidi';
import { User, Edit2, X, Database, Mail, Phone, Globe, MapPin, Hash, Link, Briefcase, Tag, Network, FileText, Trash2, Search } from 'lucide-react';
import RelationshipManager from './visualization/RelationshipManager';
import ReportGenerator from './ReportGenerator';
import TravelPatternAnalysis from './TravelPatternAnalysis';
import TransactionTable from './TransactionTable';
import EntityLedger from './EntityLedger';
import { transactionsAPI, assetsAPI, toolsAPI } from '../utils/api';
import { useData } from '../contexts/DataContext';
import { useUI } from '../contexts/UIContext';
import { useProject } from '../contexts/ProjectContext';
import { formatPersonName } from '../utils/personName';
import FilesPanel from './files/FilesPanel';
import ToolRunModal from './tools/ToolRunModal';
import { inputKindForOsint, operationsForInput, runnableTools } from '../utils/integrations';

const PersonDetailModal = () => {
  const { t } = useTranslation();
  const { people, setPeople, customFields } = useData();
  const { selectedPersonForDetail, setSelectedPersonForDetail, setEditingPerson } = useUI();
  const { activeProjectId, projects } = useProject();
  // Resolve full record (callers may pass a partial { id }). If the record
  // isn't in the (project-scoped) `people` list, it fell through to the raw
  // fetch from a deep link -- e.g. /people/:id for a person in a different
  // project than the one currently active.
  const person = (selectedPersonForDetail && people.find(p => p.id === selectedPersonForDetail.id)) || selectedPersonForDetail;
  const isOtherProject = person && activeProjectId && person.project_id != null && person.project_id !== activeProjectId;
  const otherProjectName = isOtherProject && projects.find(p => p.id === person.project_id)?.name;

  const onClose = () => setSelectedPersonForDetail(null);
  const onEdit = (p) => { setSelectedPersonForDetail(null); setEditingPerson(p); };
  const [activeTab, setActiveTab] = useState('details');
  const [showReportGenerator, setShowReportGenerator] = useState(false);
  const [locations, setLocations] = useState(person.locations || []);
  const [personTransactions, setPersonTransactions] = useState([]);
  const [personAssets, setPersonAssets] = useState([]);
  // Integrated tools usable in this person's project (issue #96).
  const [personTools, setPersonTools] = useState([]);
  const [search, setSearch] = useState(null);

  useEffect(() => {
    if (!person?.project_id) return;
    toolsAPI.getAll({ project_id: person.project_id })
      .then((list) => setPersonTools(runnableTools(list)))
      .catch(() => setPersonTools([]));
  }, [person?.project_id]);

  // Findings added from a search: update the open record and the list.
  const handleOsintAdded = (osintData) => {
    setPeople((prev) => prev.map((p) => (p.id === person.id ? { ...p, osint_data: osintData } : p)));
    setSelectedPersonForDetail({ ...person, osint_data: osintData });
  };

  useEffect(() => {
    if (!person?.id) return;
    transactionsAPI.getByPerson(person.id).then(r => setPersonTransactions(r.data || [])).catch(err => console.error('Error loading person transactions:', err));
    assetsAPI.getByPerson(person.id).then(r => setPersonAssets(r.data || [])).catch(err => console.error('Error loading person assets:', err));
  }, [person?.id]);

  const handleDeleteLocation = async (index) => {
    if (!window.confirm(t('personDetailModal.confirmRemoveLocation'))) return;
    try {
      const res = await fetch(`/api/people/${person.id}/locations/${index}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        setLocations(prev => prev.filter((_, i) => i !== index));
      } else {
        alert(t('personDetailModal.errorDeleteLocation'));
      }
    } catch {
      alert(t('personDetailModal.errorDeleteLocation'));
    }
  };
  // Removed: const [riskSummary, setRiskSummary] = useState(null);

  const getFullName = (person) => formatPersonName(person);

  const getAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getOsintIcon = (type) => {
    const icons = {
      'Social Media': <Database className="w-5 h-5" />,
      'Email': <Mail className="w-5 h-5" />,
      'Phone': <Phone className="w-5 h-5" />,
      'Website': <Globe className="w-5 h-5" />,
      'Location': <MapPin className="w-5 h-5" />,
      'Username': <Hash className="w-5 h-5" />,
    };
    return icons[type] || <Link className="w-5 h-5" />;
  };

  const getConnectedPeople = () => {
    const connected = [];
    
    // Direct connections
    if (person.connections && Array.isArray(person.connections)) {
      person.connections.forEach(conn => {
        const connectedPerson = people.find(p => p.id === conn.person_id);
        if (connectedPerson) {
          connected.push({
            person: connectedPerson,
            type: conn.type,
            note: conn.note,
            direction: 'outgoing'
          });
        }
      });
    }
    
    // Reverse connections
    people.forEach(p => {
      if (p.connections && Array.isArray(p.connections)) {
        p.connections.forEach(conn => {
          if (conn.person_id === person.id) {
            connected.push({
              person: p,
              type: conn.type,
              note: conn.note,
              direction: 'incoming'
            });
          }
        });
      }
    });
    
    return connected;
  };

  if (!person) return null;

  const connectedPeople = getConnectedPeople();

  return (
    <>
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="p-4 md:p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start gap-3">
            <div className="flex items-center space-x-4 min-w-0">
              {person.profile_picture_url ? (
                <img 
                  src={person.profile_picture_url} 
                  alt={getFullName(person)} 
                  className="w-16 h-16 rounded-full object-cover border-2 border-gray-300 dark:border-gray-600 shadow-md" 
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 flex items-center justify-center">
                  <User className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                </div>
              )}
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 break-words">
                  {getFullName(person)}
                  {person.date_of_birth && (
                    <span className="text-gray-500 dark:text-slate-400 font-normal text-lg ms-2">
                      ({getAge(person.date_of_birth)} years old)
                    </span>
                  )}
                </h2>
                {person.aliases && person.aliases.length > 0 && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">{t('peopleList.akaLabel', { aliases: isolateList(person.aliases) })}</p>
                )}
                {isOtherProject && (
                  <p className="text-xs mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400">
                    {t('personDetailModal.otherProjectBadge', { project: otherProjectName || t('personDetailModal.otherProjectUnknown') })}
                  </p>
                )}
              </div>
            </div>
            <div className="flex space-x-2 shrink-0">
              {/* Labelled, not icon-only: reporters kept concluding person
                  reports didn't exist because this was a bare icon (#63). */}
              <button
                onClick={() => setShowReportGenerator(true)}
                className="p-2 md:px-3 md:py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-accent-success hover:bg-green-600 dark:bg-green-500 hover:text-white transition-all duration-300 flex items-center gap-2 text-sm font-medium"
                title={t('personDetailModal.generateReportTitle')}
              >
                <FileText className="w-5 h-5" />
                {/* Label only where there's room; on a phone it pushed the
                    close button off the screen entirely (#64). */}
                <span className="hidden md:inline">{t('personDetailModal.generateReportTitle')}</span>
              </button>
              <button 
                onClick={() => onEdit(person)} 
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-blue-600 dark:text-blue-400 hover:bg-blue-700 dark:hover:bg-blue-600 hover:text-white transition-all duration-300"
              >
                <Edit2 className="w-5 h-5" />
              </button>
              <button 
                onClick={onClose} 
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-slate-400 hover:bg-red-600 dark:bg-red-500 hover:text-white transition-all duration-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            <div className="flex min-w-max">
            {['details', 'relationships', 'locations', 'travel', 'transactions', 'assets', 'ledger', 'files'].map((tab) => (
               <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 md:px-6 py-3 font-medium text-sm border-b-2 capitalize whitespace-nowrap transition-all duration-300 ${
                    activeTab === tab
                      ? 'border-accent-primary text-blue-600 dark:text-blue-400 bg-gradient-to-t from-white/5 to-transparent'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {t(`personDetailModal.tab_${tab}`)}
                  {tab === 'relationships' && connectedPeople.length > 0 && (
                    <span className="ms-2 text-xs glass px-2 py-1 rounded-lg text-blue-600 dark:text-blue-400">
                      {connectedPeople.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
          
          {/* Content */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
            {activeTab === 'details' && (
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold mb-3 flex items-center text-gray-900 dark:text-gray-100">
                        <User className="w-5 h-5 me-2 text-gray-400 dark:text-slate-500" />
                        {t('personDetailModal.basicInformation')}
                      </h3>
                      <div className="space-y-3 glass rounded-lg-lg p-4">
                        <div className="flex justify-between gap-3">
                          <span className="font-medium text-gray-600 dark:text-gray-400">{t('personDetailModal.firstNameLabel')}</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100 text-end break-words">{person.first_name || t('personDetailModal.notAvailable')}</span>
                        </div>
                        {/* Added with the field itself in #61 but missed here,
                            so a patronymic could be entered and never shown. */}
                        {person.patronymic && (
                          <div className="flex justify-between gap-3">
                            <span className="font-medium text-gray-600 dark:text-gray-400">{t('personForm.patronymic')}</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100 text-end break-words">{person.patronymic}</span>
                          </div>
                        )}
                        <div className="flex justify-between gap-3">
                          <span className="font-medium text-gray-600 dark:text-gray-400">{t('personDetailModal.lastNameLabel')}</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100 text-end break-words">{person.last_name || t('personDetailModal.notAvailable')}</span>
                        </div>
                        {person.date_of_birth && (
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-600 dark:text-gray-400">{t('personDetailModal.dateOfBirthLabel')}</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{new Date(person.date_of_birth).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3 flex items-center text-gray-900 dark:text-gray-100">
                        <Tag className="w-5 h-5 me-2 text-gray-400 dark:text-slate-500" />
                        {t('personDetailModal.classification')}
                      </h3>
                      <div className="space-y-3 glass rounded-lg-lg p-4">
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600 dark:text-gray-400">{t('personDetailModal.categoryLabel')}</span>
                          <span className="px-3 py-1 bg-blue-600 text-white dark:bg-blue-500 rounded-lg text-sm font-medium">
                            {person.category ? optionLabel(t, 'person_category', person.category) : t('personDetailModal.notAvailable')}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600 dark:text-gray-400">{t('personDetailModal.statusLabel')}</span>
                          <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                            person.status === 'Open' ? 'bg-green-600 dark:bg-green-500 text-white' :
                            person.status === 'Being Investigated' ? 'bg-accent-warning text-white' :
                            person.status === 'Closed' ? 'glass text-gray-800 dark:text-slate-200' :
                            'bg-blue-600 text-white dark:bg-blue-500'
                          }`}>
                            {person.status ? optionLabel(t, 'person_status', person.status) : t('personDetailModal.notAvailable')}
                          </span>
                        </div>
                        {person.crm_status && (
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-600 dark:text-gray-400">{t('personDetailModal.crmStatusLabel')}</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{optionLabel(t, 'crm_status', person.crm_status)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Case and Additional Info */}
                  <div className="space-y-4">
                    {person.case_name && (
                      <div>
                        <h3 className="text-lg font-semibold mb-3 flex items-center text-gray-900 dark:text-gray-100">
                          <Briefcase className="w-5 h-5 me-2 text-gray-400 dark:text-slate-500" />
                          {t('personDetailModal.caseInformation')}
                        </h3>
                        <div className="glass rounded-lg-lg p-4">
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-600 dark:text-gray-400">{t('personDetailModal.caseNameLabel')}</span>
                            <span className="text-blue-600 dark:text-blue-400 font-semibold">{person.case_name}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <h3 className="text-lg font-semibold mb-3 flex items-center text-gray-900 dark:text-gray-100">
                        <Network className="w-5 h-5 me-2 text-gray-400 dark:text-slate-500" />
                        {t('personDetailModal.connectionSummary')}
                      </h3>
                      <div className="glass rounded-lg-lg p-4">
                        <div className="text-center">
                          <div className="text-3xl font-bold bg-gradient-to-r from-accent-primary to-accent-secondary bg-clip-text text-transparent">{connectedPeople.length}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">{t('personDetailModal.totalConnections')}</div>
                        </div>
                        {connectedPeople.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                              <div className="font-medium text-gray-900 dark:text-gray-100">{t('personDetailModal.outgoingLabel')} <span className="text-blue-600 dark:text-blue-400">{connectedPeople.filter(c => c.direction === 'outgoing').length}</span></div>
                              <div className="font-medium text-gray-900 dark:text-gray-100">{t('personDetailModal.incomingLabel')} <span className="text-accent-secondary">{connectedPeople.filter(c => c.direction === 'incoming').length}</span></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {person.notes && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">{t('propertyDetailModal.notesLabel')}</h3>
                    <div className="glass rounded-lg-lg p-4">
                      <p dir="auto" className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">{person.notes}</p>
                    </div>
                  </div>
                )}

                {/* OSINT Data */}
                {((person.osint_data && person.osint_data.length > 0) || personTools.length > 0) && (
                  <div className="mb-6">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('personDetailModal.osintData')}</h3>
                      {personTools.length > 0 && (
                        <button type="button" onClick={() => setSearch({ index: null })} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                          <Search className="w-4 h-4" />{t('tools.searchWithTools')}
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {(person.osint_data || []).map((osint, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 glass rounded-lg hover:bg-gray-100 dark:bg-gray-700 transition-all duration-300">
                          <div className="text-gray-600 dark:text-gray-400">
                            {getOsintIcon(osint.type)}
                          </div>
                          <div className="flex-1 text-gray-900 dark:text-gray-100">
                            <span className="font-medium">{optionLabel(t, 'osint_data_type', osint.type)}:</span> <bdi>{osint.value}</bdi>
                            {osint.notes && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{osint.notes}</p>}
                            {osint.source && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {t('tools.provenance', { tool: osint.source.tool_name || osint.source.integration_id, date: (osint.source.retrieved_at || '').slice(0, 10) })}
                                {osint.verified === false && <span className="ms-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">{t('tools.unverified')}</span>}
                              </p>
                            )}
                          </div>
                          {operationsForInput(personTools, { type: osint.type, value: osint.value, kind: inputKindForOsint(osint.type, osint.value) }).length > 0 && (
                            <button type="button" onClick={() => setSearch({ index })} title={t('tools.searchThis')} aria-label={t('tools.searchThis')} className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                              <Search className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Custom Fields */}
                {person.custom_fields && Object.keys(person.custom_fields).length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">{t('personDetailModal.customFields')}</h3>
                    <div className="space-y-2">
                      {Object.entries(person.custom_fields).map(([key, value]) => {
                        const fieldDef = customFields.find(f => f.field_name === key);
                        return (
                          <div key={key} className="flex items-center space-x-3 p-3 glass rounded-lg text-gray-900 dark:text-gray-100">
                            <span className="font-medium">{fieldDef?.field_label || key}:</span>
                            <span>{value}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'relationships' && (
              <div className="h-[500px]">
                <RelationshipManager 
                  personId={person.id} 
                  showInModal={true}
                  onClose={() => setActiveTab('details')}
                />
              </div>
            )}
            
            {activeTab === 'locations' && (
              <div className="p-6">
                {locations.length > 0 ? (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">{t('personDetailModal.locationsTitle')}</h3>
                    <div className="space-y-3">
                      {locations.map((location, index) => (
                        <div key={index} className="p-4 glass rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-2">
                                <MapPin className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                                <span className="font-medium text-sm px-3 py-1 bg-blue-600 text-white dark:bg-blue-500 rounded-lg">
                                  {(location.type || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                              </div>
                              <p className="font-medium text-gray-900 dark:text-gray-100">{location.address}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {[location.city, location.state, location.country, location.postal_code]
                                  .filter(Boolean)
                                  .join(', ')}
                              </p>
                              {location.notes && <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{location.notes}</p>}
                            </div>
                            <button
                              onClick={() => handleDeleteLocation(index)}
                              className="ms-3 p-1.5 text-gray-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 transition-colors duration-150 flex-shrink-0"
                              title={t('personDetailModal.removeLocationTitle')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    {t('personDetailModal.noLocationData')}
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'travel' && (
              <div className="p-6">
                <TravelPatternAnalysis
                  personId={person.id}
                  personName={getFullName(person)}
                />
              </div>
            )}

            {activeTab === 'transactions' && (
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">{t('personDetailModal.receivedTitle')}</h3>
                  <TransactionTable transactions={personTransactions.filter(txn => txn.direction === 'received')} emptyText={t('personDetailModal.nothingReceived')} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">{t('personDetailModal.givenTitle')}</h3>
                  <TransactionTable transactions={personTransactions.filter(txn => txn.direction === 'given')} emptyText={t('personDetailModal.nothingGiven')} />
                </div>
              </div>
            )}

            {activeTab === 'assets' && (
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">{t('personDetailModal.assetsCurrentlyHeld')}</h3>
                {personAssets.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">{t('personDetailModal.noAssetsHeld')}</p>
                ) : (
                  <div className="space-y-2">
                    {personAssets.map(a => (
                      <div key={a.id} className="flex justify-between items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
                        <span className="text-gray-800 dark:text-gray-200 font-medium">{a.name}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{[a.category, a.status].filter(Boolean).join(' · ')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'ledger' && (
              <div className="p-6">
                <EntityLedger entityType="people" entityId={person.id} />
              </div>
            )}

            {activeTab === 'files' && (
              <div className="p-6">
                <FilesPanel personId={person.id} projectId={person.project_id} />
              </div>
            )}
          </div>
        </div>
      </div>

      {search && (
        <ToolRunModal
          tools={personTools}
          person={person}
          initialEntryIndex={search.index}
          onClose={() => setSearch(null)}
          onAdded={handleOsintAdded}
        />
      )}

      {/* Report Generator Modal */}
      {showReportGenerator && (
        <ReportGenerator 
          personId={person.id}
          onClose={() => setShowReportGenerator(false)}
        />
      )}
    </>
  );
};

export default PersonDetailModal;